import type { ReactNode } from 'react';
import type { MsrBaumKnoten } from './typen';
import { z } from './gemeinsam';
import { StatusBadge } from '../../muster';

/**
 * Ein Knoten des MSR-Baums — rekursiv, reines Anzeigemuster (Muster
 * `koordination/Baum.tsx`). Zahlen je Knoten (Summe Datenpunkte), geschätzt
 * als gelbe kleine Marke, Warnung rot.
 */
export function Knoten({
  knoten, ebene, gewaehlt, onWaehlen, offen, onToggle, suche,
}: {
  knoten: MsrBaumKnoten;
  ebene: number;
  gewaehlt: string | null;
  onWaehlen: (id: string, art: MsrBaumKnoten['art']) => void;
  offen: Set<string>;
  onToggle: (id: string) => void;
  suche: string;
}): ReactNode {
  const passt = (k: MsrBaumKnoten): boolean => {
    if (!suche.trim()) return true;
    const s = suche.trim().toLowerCase();
    if (k.bezeichnung.toLowerCase().includes(s)) return true;
    return (k.kinder ?? []).some(passt);
  };

  if (!passt(knoten)) return null;

  const istBlatt = knoten.art === 'aggregat' || (knoten.kinder ?? []).length === 0;
  const auf = offen.has(knoten.id);

  return (
    <div key={knoten.id}>
      <button
        type="button"
        className={istBlatt
          ? `baum-zeile${gewaehlt === knoten.id ? ' aktiv' : ''}`
          : 'baum-ebene'}
        style={{ paddingLeft: 12 + ebene * 14, width: '100%' }}
        onClick={() => (istBlatt
          ? onWaehlen(knoten.id, knoten.art)
          : (onToggle(knoten.id), onWaehlen(knoten.id, knoten.art)))}
      >
        {!istBlatt && <span style={{ width: 12 }}>{auf ? '▾' : '▸'}</span>}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                       whiteSpace: 'nowrap' }}>
          {knoten.bezeichnung}
        </span>
        {knoten.zusatz && (
          <span className="schwach" style={{ fontSize: 11 }}>{knoten.zusatz}</span>
        )}

        {knoten.geschaetzt > 0 && (
          <StatusBadge klein zustand="warnung" text={`${z(knoten.geschaetzt)} geschätzt`} />
        )}

        {knoten.warnung && (
          <StatusBadge klein zustand="fehler" text={knoten.warnung} />
        )}

        <span className="schwach">{z(knoten.zahlen?.summe)}</span>
      </button>

      {!istBlatt && auf && (knoten.kinder ?? []).map((k) => (
        <Knoten key={k.id} knoten={k} ebene={ebene + 1} gewaehlt={gewaehlt}
          onWaehlen={onWaehlen} offen={offen} onToggle={onToggle} suche={suche} />
      ))}
    </div>
  );
}
